import { BadgeCheck, CircleSlash, PackageCheck, ShoppingBag, Trash2, XCircle } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { fmtDate, fmtDateTime, fmtMoney } from '../../shared/format'
import type { Order, OrderStatus } from '../types'
import { OrderStatusBadge } from './CrmBits'
import { CustomerLink } from './RecordLists'

const ICON_TONE: Record<OrderStatus, string> = {
  pending: 'bg-warning/15 text-warning-strong',
  approved: 'bg-success/12 text-success',
  rejected: 'bg-destructive/10 text-destructive',
}

function Fact({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="truncate text-sm font-medium">{value}</dd>
    </div>
  )
}

type Props = { order: Order; showCustomer?: boolean; canWrite: boolean; canApprove: boolean; onApprove: () => void; onReject: () => void; onDelete: () => void }

/** Sipariş kartı: sabitlenmiş kalemler, tutar, lisans kapsamı ve onay/ret işlemleri. */
export function OrderCard({ order: o, showCustomer, canWrite, canApprove, onApprove, onReject, onDelete }: Props) {
  const { t } = useTranslation()
  const l = o.license

  return (
    <article className="flex min-w-0 flex-col overflow-hidden rounded-xl border bg-card shadow-card transition-shadow hover:shadow-card-hover">
      <header className="flex items-start gap-3 p-4 pb-3">
        <span className={cn('flex size-10 shrink-0 items-center justify-center rounded-lg', ICON_TONE[o.status])}>
          <ShoppingBag className="size-5" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-mono text-sm font-semibold">{o.number}</h3>
            <OrderStatusBadge status={o.status} />
          </div>
          <p className="mt-0.5 truncate text-xs text-muted-foreground">
            {showCustomer ? (
              <>
                <CustomerLink id={o.customerId} tab="orders" /> ·{' '}
              </>
            ) : null}
            {t('crm.order.fromOfferShort', { number: o.offerNumber })} · {fmtDate(o.createdAt.slice(0, 10))} · {o.createdBy}
          </p>
        </div>
        <p className="shrink-0 text-right text-lg font-semibold tabular-nums">{fmtMoney(o.total)}</p>
      </header>

      <ul className="mx-4 divide-y rounded-lg border text-sm">
        {o.items.map((i) => (
          <li key={i.id} className="flex items-center gap-3 px-3 py-1.5">
            <span className="min-w-0 flex-1 truncate">
              {i.name} <span className="text-xs text-muted-foreground">× {i.qty}</span>
            </span>
            <span className="shrink-0 tabular-nums text-muted-foreground">{fmtMoney(o.lineTotals[i.id] ?? '0')}</span>
          </li>
        ))}
      </ul>

      {l ? (
        <dl className="mx-4 mt-3 grid grid-cols-2 gap-x-4 gap-y-2 rounded-lg bg-success/[0.06] p-3 sm:grid-cols-3">
          <Fact label={t('crm.order.branchLimit')} value={t('crm.order.branches', { count: l.branchLimit })} />
          <Fact label={t('crm.order.term')} value={t(`crm.licenseTerm.${l.term}`)} />
          <Fact label={t('crm.order.validUntil')} value={l.validUntil ? fmtDate(l.validUntil) : t('crm.order.noEnd')} />
          <Fact label={t('crm.order.maintenanceUntil')} value={l.maintenanceUntil ? fmtDate(l.maintenanceUntil) : '—'} />
          <Fact label={t('crm.order.supportUntil')} value={l.supportUntil ? fmtDate(l.supportUntil) : '—'} />
        </dl>
      ) : null}

      {o.status === 'rejected' && o.decision ? (
        <p className="mx-4 mt-3 flex gap-2 rounded-lg bg-destructive/[0.06] p-3 text-sm">
          <CircleSlash className="mt-0.5 size-4 shrink-0 text-destructive" aria-hidden />
          <span className="min-w-0">{o.decision.note}</span>
        </p>
      ) : null}

      <footer className="mt-auto flex flex-wrap items-center justify-end gap-2 p-4 pt-3">
        {o.decision ? (
          <span className="mr-auto inline-flex items-center gap-1.5 text-xs text-muted-foreground">
            <PackageCheck className="size-4" aria-hidden />
            {t(o.status === 'approved' ? 'crm.order.approvedBy' : 'crm.order.rejectedBy', { by: o.decision.by, at: fmtDateTime(o.decision.at) })}
          </span>
        ) : (
          <span className="mr-auto text-xs text-muted-foreground">{t('crm.order.waiting')}</span>
        )}
        {canWrite && o.status !== 'approved' ? (
          <Button size="icon" variant="ghost" className="size-8 hover:text-destructive" aria-label={t('crm.del.order')} onClick={onDelete}>
            <Trash2 />
          </Button>
        ) : null}
        {canApprove && o.status === 'pending' ? (
          <>
            <Button size="sm" variant="outline" onClick={onReject}>
              <XCircle />
              {t('crm.order.reject')}
            </Button>
            <Button size="sm" onClick={onApprove}>
              <BadgeCheck />
              {t('crm.order.approve')}
            </Button>
          </>
        ) : null}
      </footer>
    </article>
  )
}
